import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { NavigationEnd, Router, RouterLink } from '@angular/router';
import { interval, startWith } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { notificationLevelIcon } from './notification-level.util';
import { relativeTimeKo } from './notification-time.util';
import { NOTIFICATIONS_API_CLIENT, type NotificationListItem } from './notifications-api-client';

const RECENT_LIMIT = 20;
/** 헤더에 항상 떠 있는 컴포넌트가 서버를 계속 두드리지 않도록 충분히 긴 간격의 안읽음
 * 개수 안전망 폴링 — 실시간성이 필요한 화면은 아니다. 실사용 중에는 아래 라우트 전환
 * 리프레시가 대부분의 케이스를 즉시 반영한다. */
const POLL_INTERVAL_MS = 60_000;

/**
 * 헤더 알림 벨 — 안읽음 배지 + 최근 알림 드롭다운. `NOTIFICATIONS_API_CLIENT` 토큰으로
 * 알림 API를 주입받는다(각 소비 앱이 자기 앱 백엔드용 구현을 `useExisting`으로 연결,
 * notifications-api-client.ts 상단 주석 참고) — 통합 인박스(org 스코프 없음)라 org 컨텍스트
 * 전환과 무관하게 항상 같은 목록을 보여준다.
 *
 * `viewAllRoute`가 있으면 패널 하단에 전체 목록 페이지로 가는 링크를 보여준다 — 없는
 * 앱(아직 전체 목록 화면을 안 만든 경우)은 생략된다.
 */
@Component({
  selector: 'spinoff-notification-bell',
  standalone: true,
  imports: [MatBadgeModule, MatButtonModule, MatIconModule, MatMenuModule, MatProgressSpinnerModule, RouterLink],
  templateUrl: './notification-bell.component.html',
  styles: [
    `
      :host {
        display: contents;
      }
      .nb__panel {
        width: 360px;
        max-width: calc(100vw - 32px);
      }
      .nb__header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 4px 12px 4px 16px;
      }
      .nb__title {
        font-weight: 600;
        font-size: 0.9375rem;
      }
      .nb__state {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 24px 16px;
        color: var(--mat-sys-on-surface-variant);
        font-size: 0.875rem;
      }
      .nb__item {
        display: flex !important;
        align-items: flex-start;
        gap: 10px;
        min-height: unset !important;
        height: auto !important;
        padding: 10px 16px !important;
        white-space: normal !important;
        line-height: normal;
      }
      .nb__item-icon {
        flex-shrink: 0;
        margin-top: 2px;
        font-size: 20px;
        width: 20px;
        height: 20px;
      }
      .nb__item-icon--info {
        color: var(--mat-sys-primary);
      }
      .nb__item-icon--success {
        color: var(--mat-sys-tertiary);
      }
      .nb__item-icon--warning {
        color: var(--mat-sys-secondary);
      }
      .nb__item-icon--error {
        color: var(--mat-sys-error);
      }
      .nb__item-body {
        display: flex;
        flex-direction: column;
        gap: 2px;
        overflow: hidden;
        min-width: 0;
        text-align: left;
      }
      .nb__item-title {
        font-size: 0.875rem;
        font-weight: 400;
        overflow: hidden;
        text-overflow: ellipsis;
      }
      .nb__item--unread .nb__item-title {
        font-weight: 700;
      }
      .nb__item-time {
        font-size: 0.75rem;
        color: var(--mat-sys-on-surface-variant);
      }
      .nb__item-dot {
        flex-shrink: 0;
        width: 8px;
        height: 8px;
        border-radius: 50%;
        background: var(--mat-sys-primary);
        margin-top: 6px;
      }
    `,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SpinoffNotificationBellComponent {
  private readonly api = inject(NOTIFICATIONS_API_CLIENT);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  /** 패널 하단 "전체 보기" 링크 대상. 생략하면 링크 자체를 숨긴다. */
  readonly viewAllRoute = input<string | null>(null);

  protected readonly unreadCount = signal(0);
  protected readonly items = signal<NotificationListItem[]>([]);
  protected readonly loading = signal(false);
  protected readonly loadedOnce = signal(false);

  protected readonly levelIcon = notificationLevelIcon;
  protected readonly relativeTime = relativeTimeKo;

  constructor() {
    // startWith(0)으로 구독 즉시 1회 발행 — 메뉴를 한 번도 안 열어도 배지 숫자는 바로 보여야 한다.
    interval(POLL_INTERVAL_MS)
      .pipe(startWith(0), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.refreshUnreadCount());

    // 다른 화면(전체 목록의 "모두 읽음" 등)에서 읽음 상태가 바뀌어도 이 벨은 별도 컴포넌트
    // 인스턴스라 자동으로 알지 못한다 — 라우트 전환마다 다시 조회해 배지가 스스로
    // 맞춰지게 한다(sms-user-web 선례). 경로(path)가 실제로 바뀔 때만 반응한다 — 쿼리
    // 파라미터만 바뀌는 replaceUrl 네비게이션(예: 시간표 뷰의 이전/다음 날짜 이동)까지
    // 매번 재조회하면 알림과 무관한 화면 조작마다 불필요한 API 호출이 생긴다(code-review 지적).
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        map((e) => e.urlAfterRedirects.split('?')[0]),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.refreshUnreadCount());
  }

  onMenuOpened(): void {
    this.loading.set(true);
    this.api.list({ page: 1, size: RECENT_LIMIT }).subscribe({
      next: (res) => {
        this.items.set(res.items);
        this.loading.set(false);
        this.loadedOnce.set(true);
      },
      error: () => {
        this.loading.set(false);
        this.loadedOnce.set(true);
      },
    });
  }

  onItemClick(item: NotificationListItem): void {
    if (!item.readAt) this.markReadLocally(item.id);
    if (item.linkPath) void this.router.navigateByUrl(item.linkPath);
  }

  markAllRead(): void {
    if (this.unreadCount() === 0) return;
    // 배지 카운트 갱신용 best-effort 호출 — 실패해도 사용자에게 보여줄 에러 UI가 없어
    // refreshUnreadCount()와 동일하게 조용히 무시한다(에러 콜백 없으면 RxJS가
    // reportUnhandledError로 다시 던져 콘솔에 unhandled error가 뜬다).
    this.api.markAllRead().subscribe({
      next: () => {
        const now = new Date().toISOString();
        this.items.update((list) => list.map((it) => (it.readAt ? it : { ...it, readAt: now })));
        this.unreadCount.set(0);
      },
      error: () => undefined,
    });
  }

  private markReadLocally(id: string): void {
    this.api.markRead(id).subscribe({
      next: () => {
        const now = new Date().toISOString();
        this.items.update((list) => list.map((it) => (it.id === id ? { ...it, readAt: now } : it)));
        this.unreadCount.update((n) => Math.max(0, n - 1));
      },
      error: () => undefined,
    });
  }

  private refreshUnreadCount(): void {
    this.api.unreadCount().subscribe({
      next: (res) => this.unreadCount.set(res.count),
      error: () => undefined,
    });
  }
}
